// Comparing a submitted translation against the expected one.
//
// Deliberately dependency-free — this is one small function, and pulling in
// jsdiff would add far more bundle than it saves in code.
//
// Granularity has to vary by language. Splitting on whitespace gives a single
// token for Japanese or Chinese, so the entire sentence would come back marked
// as wrong — which is the exact problem this is meant to fix. Those languages
// diff per character instead.

// Shared by the diff and by the completion check in Sentence.jsx, so the two
// can't drift apart. Collapsing whitespace matters because JS \s covers NBSP
// and the ideographic space U+3000: a typed space then equals a target NBSP,
// which is what made French and Japanese sentences impossible to finish.
export function normalize(text) {
  return String(text == null ? '' : text).replace(/\s+/g, ' ').trim();
}

// Whether two whole strings count as the same answer. Case folding lives here
// rather than in normalize() because the comparison and the display have
// different needs: the diff must fold case to decide what's wrong, but has to
// keep the original casing to show it back.
export function sameText(a, b, { caseSensitive = true } = {}) {
  const x = normalize(a);
  const y = normalize(b);
  return caseSensitive ? x === y : x.toLowerCase() === y.toLowerCase();
}

export function tokenize(text, spaceless) {
  const normalized = normalize(text);
  if(!normalized) {
    return [];
  }
  return spaceless ? Array.from(normalized) : normalized.split(' ');
}

// Standard longest-common-subsequence table, filled from the end so the
// walk below can move forward greedily. Sentences are short enough that the
// O(n*m) cost is irrelevant.
function lcsTable(a, b, isEqual) {
  const table = Array.from({ length: a.length + 1 }, () => new Array(b.length + 1).fill(0));
  for(let i = a.length - 1; i >= 0; i--) {
    for(let j = b.length - 1; j >= 0; j--) {
      table[i][j] = isEqual(a[i], b[j])
        ? table[i + 1][j + 1] + 1
        : Math.max(table[i + 1][j], table[i][j + 1]);
    }
  }
  return table;
}

const STRICT = (a, b) => a === b;

// Returns a flat, in-order list of { op, token }:
//   equal   — present in both
//   missing — in the expected sentence, absent from the attempt
//   extra   — in the attempt, not in the expected sentence
export function diffTokens(expected, actual, isEqual = STRICT) {
  const table = lcsTable(expected, actual, isEqual);
  const ops = [];
  let i = 0;
  let j = 0;

  while(i < expected.length && j < actual.length) {
    if(isEqual(expected[i], actual[j])) {
      // Two tokens can be "equal" and still differ — case folding is the reason
      // this exists. The attempt line has to show what was actually typed, so
      // keep the other spelling when there is one.
      const op = { op: 'equal', token: expected[i] };
      if(expected[i] !== actual[j]) {
        op.actualToken = actual[j];
      }
      ops.push(op);
      i++;
      j++;
    }
    else if(table[i + 1][j] >= table[i][j + 1]) {
      ops.push({ op: 'missing', token: expected[i] });
      i++;
    }
    else {
      ops.push({ op: 'extra', token: actual[j] });
      j++;
    }
  }
  while(i < expected.length) {
    ops.push({ op: 'missing', token: expected[i] });
    i++;
  }
  while(j < actual.length) {
    ops.push({ op: 'extra', token: actual[j] });
    j++;
  }

  return ops;
}

// The two lines the comparison panel renders. Splitting here rather than in the
// component keeps the "which ops belong on which line" rule in one place:
// the attempt shows what you typed, the expected shows what was wanted, and
// each marks only the tokens the other lacks.
export function compare(expectedText, actualText, { spaceless = false, caseSensitive = true } = {}) {
  const expected = tokenize(expectedText, spaceless);
  const actual = tokenize(actualText, spaceless);
  // Tokens keep their original casing so the panel can show what was actually
  // typed; only the equality test folds case.
  const isEqual = caseSensitive ? STRICT : (a, b) => a.toLowerCase() === b.toLowerCase();
  const ops = diffTokens(expected, actual, isEqual);

  return {
    spaceless,
    identical: ops.every((o) => o.op === 'equal'),
    attempt: ops
      .filter((o) => o.op !== 'missing')
      .map((o) => ({ op: o.op, token: o.actualToken === undefined ? o.token : o.actualToken, wrong: o.op === 'extra' })),
    expected: ops
      .filter((o) => o.op !== 'extra')
      .map((o) => ({ op: o.op, token: o.token, wrong: o.op === 'missing' })),
  };
}
